<script lang="ts">
	import { appStore } from '../stores/appStore';
	import { themeStore } from '../stores/themeStore';
	import { AppApiClient } from '../api_contracts/app.client';
	import { FileDetailsViewProps } from '../models/props';
	import MonacoEditor from './MonacoEditor.svelte';

	let { file, branch, onBack, currentPath = [], repository }: FileDetailsViewProps = $props();

	// Construct full file path - derived from props
	const fullPath = $derived(currentPath.length > 0
		? currentPath.join('/') + '/' + file.name
		: file.name);

	let fileContent = $state('');
	let loading = $state(true);
	let error = $state('');
	let client: AppApiClient | null = $state(null);
	let token: string = $state('');
	let profile: any = $state(null);
	let userTheme = $state<'light' | 'dark'>('dark');

	// Subscribe to stores
	appStore.subscribe((state) => {
		profile = state.profile;
		client = state.client;
		token = state.token;
	});

	themeStore.subscribe((theme) => {
		userTheme = theme;
	});

	// Convert theme to Monaco theme
	const monacoTheme = $derived(userTheme === 'dark' ? 'vs-dark' : 'vs');

	const getFileIcon = (filename: string) => {
		const ext = filename.split('.').pop()?.toLowerCase();
		const icons: Record<string, string> = {
			js: '📄',
			ts: '📘',
			svelte: '🔶',
			json: '📋',
			md: '📝',
			css: '🎨',
			html: '🌐',
			py: '🐍',
			default: '📄'
		};
		return icons[ext || 'default'] || icons.default;
	};

	// Detect language from file extension
	const getLanguage = (filename: string): string => {
		const ext = filename.split('.').pop()?.toLowerCase() || '';
		const languageMap: Record<string, string> = {
			// JavaScript/TypeScript
			js: 'javascript',
			jsx: 'javascript',
			ts: 'typescript',
			tsx: 'typescript',

			// Web
			html: 'html',
			htm: 'html',
			css: 'css',
			scss: 'scss',
			sass: 'sass',
			less: 'less',

			// Data formats
			json: 'json',
			xml: 'xml',
			yaml: 'yaml',
			yml: 'yaml',
			toml: 'toml',

			// Python
			py: 'python',
			pyw: 'python',

			// Java/Kotlin
			java: 'java',
			kt: 'kotlin',
			kts: 'kotlin',

			// C/C++
			c: 'c',
			cpp: 'cpp',
			cc: 'cpp',
			cxx: 'cpp',
			h: 'c',
			hpp: 'cpp',

			// C#
			cs: 'csharp',

			// Go
			go: 'go',

			// Rust
			rs: 'rust',

			// Ruby
			rb: 'ruby',

			// PHP
			php: 'php',

			// Shell
			sh: 'shell',
			bash: 'shell',
			zsh: 'shell',

			// SQL
			sql: 'sql',

			// Markdown
			md: 'markdown',
			markdown: 'markdown',

			// Configuration
			conf: 'ini',
			ini: 'ini',
			cfg: 'ini',

			// Docker
			dockerfile: 'dockerfile',

			// Svelte
			svelte: 'html', // Monaco doesn't have native Svelte support, use HTML as fallback

			// Vue
			vue: 'html',

			// Other
			txt: 'plaintext',
			log: 'plaintext',
		};

		return languageMap[ext] || 'plaintext';
	};

	async function fetchFileContent() {
		if (!client || !token || !profile || !repository) {
			error = 'Missing required data';
			loading = false;
			return;
		}

		try {
			loading = true;
			error = '';

			const result = await client.getFileContent({
				token,
				ownerId: profile.id,
				repositoryName: repository.name,
				filePath: fullPath,
				branch: branch
			});

			if (result.response.successFull) {
				fileContent = result.response.content || '';
			} else {
				error = result.response.reason || 'Failed to fetch file content';
			}
		} catch (e: any) {
			error = e.message || 'Error fetching file content';
			console.error('Error fetching file content:', e);
		} finally {
			loading = false;
		}
	}

	// Watch for changes to file and fetch content when file changes
	$effect(() => {
		if (file.type === 'file' && client && token && profile && repository) {
			fetchFileContent();
		} else if (file.type !== 'file') {
			loading = false;
		}
	});
</script>

<div class="mb-8">
	<!-- Back Button -->
	<!-- <div class="mb-4">
		<button
			onclick={onBack}
			class="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
		>
			<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
			</svg>
			<span class="font-medium">Back</span>
		</button>
	</div> -->

	<!-- Breadcrumb Navigation -->
	<div class="mb-4 flex items-center justify-between">
		<div class="flex items-center space-x-2">
			<!-- <button class="flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded border border-gray-300 transition-colors text-sm font-medium">
				<svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
					<path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"/>
				</svg>
				{branch}
			</button> -->
			<span class="text-gray-400">/</span>
			<span class="text-gray-700 font-medium">{fullPath}</span>
		</div>

		<div class="flex items-center space-x-2">
			<button class="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded border border-gray-300 text-sm transition-colors font-medium">
				Copy path
			</button>
			<button class="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded border border-gray-300 text-sm transition-colors font-medium">
				<svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
					/>
				</svg>
				Download
			</button>

			{#if repository}
				<a
					href={`http://localhost:8443/?folder=/config/workspace/${repository.name}`}
					target="_blank"
					class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded border border-blue-600 text-sm transition-colors font-medium flex items-center decoration-0"
				>
					<svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
						/>
					</svg>
					Open in online editor
				</a>
			{/if}
		</div>
	</div>

	<!-- File Container -->
	<div class="border border-gray-300 rounded-md overflow-hidden bg-white">
		<!-- Commit Info Bar -->
		<div class="px-4 py-2.5 bg-gray-50 border-b border-gray-300 flex justify-between items-center">
			<div class="flex items-center space-x-3">
				<div class="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-semibold">
					AK
				</div>
				<div class="flex items-center space-x-2 text-sm">
					<a href="#" class="text-blue-600 hover:underline font-semibold">Arthur-Kamau</a>
					<span class="text-gray-700">{file.message}</span>
				</div>
			</div>
			<div class="flex items-center space-x-4 text-sm text-gray-600">
				<span>{file.updatedAt}</span>
			</div>
		</div>

		{#if file.type === 'file'}
			<!-- File Tabs -->
			<div class="flex items-center space-x-6 px-4 py-2 bg-gray-50 border-b border-gray-300 text-sm">
				<button class="text-gray-800 font-semibold border-b-2 border-orange-500 pb-2 -mb-[1px]">
					Code
				</button>
				<button class="text-gray-600 hover:text-gray-800 pb-2">
					Blame
				</button>
			</div>

			{#if loading}
				<!-- Loading State -->
				<div class="p-8 text-center">
					<div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
					<p class="mt-4 text-gray-600">Loading file content...</p>
				</div>
			{:else if error}
				<!-- Error State -->
				<div class="p-8 text-center">
					<svg class="w-12 h-12 mx-auto text-red-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
						<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
					</svg>
					<p class="text-red-600 font-semibold">{error}</p>
				</div>
			{:else}
				<!-- File Stats Bar -->
				<div class="px-4 py-2 bg-gray-50 border-b border-gray-300 flex justify-between items-center text-xs text-gray-600">
					<span>{fileContent.split('\n').length} lines • {new Blob([fileContent]).size} Bytes • {getLanguage(file.name).toUpperCase()}</span>
					<div class="flex items-center space-x-3">
						<button class="hover:text-blue-600 font-medium">Raw</button>
						<button class="hover:text-blue-600 font-medium">Copy</button>
					</div>
				</div>

				<!-- Monaco Editor -->
				<div class="monaco-wrapper" style="height: 600px;">
					<MonacoEditor
						value={fileContent}
						language={getLanguage(file.name)}
						theme={monacoTheme}
						readOnly={true}
					/>
				</div>
			{/if}
		{:else}
			<!-- Folder Content -->
			<div class="p-8 text-center text-gray-600">
				<svg class="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
					<path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
				</svg>
				<p class="text-lg font-medium text-gray-800">This is a folder</p>
				<p class="text-sm mt-2">Folder navigation will be implemented with API integration</p>
			</div>
		{/if}
	</div>
</div>
